import { useMutation } from '@apollo/client';
import { Stack } from '@ui-components/neo.tax';
import React from 'react';
import { useForm } from 'react-hook-form';

import {
  GetUserQueryVariables,
  GET_USER,
  UpdateUserAccessInOrganizationMutation,
  UpdateUserAccessInOrganizationMutationVariables,
  REPLACE_USER_ACCESS_IN_ORGANIZATION,
} from '@neo.tax/shared/graphql';
import { useCurrentUser } from '@neo.tax/shared/hooks';
import { OrganizationMemberItemScope, OrganizationMemberTableItemMember } from '@neo.tax/shared/types';

import { convertFormStateToOrganizationInvitationInput } from '../edit-organization-member-modal/edit-organization-member-modal.utils';
import { MemberScopeTable } from '../member-scope-table';
import { StyledButton, StyledStack } from './member-scope-pane.styled';

interface MemberScopeProps {
  member: OrganizationMemberTableItemMember;
  organizationId: string | undefined;
}

export const MemberScopePane = ({ member, organizationId }: MemberScopeProps): React.ReactElement => {
  const { user } = useCurrentUser();
  const [replaceUserAccessInOrganization, { loading }] = useMutation<
    UpdateUserAccessInOrganizationMutation,
    UpdateUserAccessInOrganizationMutationVariables
  >(REPLACE_USER_ACCESS_IN_ORGANIZATION);

  const { formState, handleSubmit, reset, setValue, watch } = useForm<{ companyIds: string[] }>({
    mode: 'onChange',
    defaultValues: { companyIds: member.companies.map((company) => company.id) },
  });

  const companyIds = watch('companyIds');

  const handleCompanySelect = (companyIds: string[]): void => {
    setValue('companyIds', companyIds, { shouldValidate: true, shouldDirty: true });
  };

  const onSubmit = async (values: { companyIds: string[] }): Promise<void> => {
    if (!organizationId || !user) {
      return;
    }

    // Reusing the invitation form transform to derive organizationScope/companyScope is
    // convenient but expirationDays isn't used here (we're editing an accepted member's
    // access, not creating an invitation). Pass a safe placeholder; it's discarded below.
    const invitationInput = convertFormStateToOrganizationInvitationInput({
      ...member,
      companyIds: values.companyIds,
      expirationDays: '7',
    });
    await replaceUserAccessInOrganization({
      variables: {
        organizationId,
        userId: member.id,
        replaceUserAccessInOrganizationInput: {
          organizationScope: invitationInput.organizationScope,
          companyIds: invitationInput.companyIds,
          companyScope: invitationInput.companyScope,
        },
      },
      refetchQueries: [
        {
          query: GET_USER,
          variables: {
            userId: user.id,
          } satisfies GetUserQueryVariables,
        },
      ],
      awaitRefetchQueries: true,
      context: {
        errorToast: {
          title: 'Error updating member access',
          description: 'Something unexpected prevented your changes from saving. Please try again.',
        },
        successToast: {
          title: 'Member access updated',
          description: 'Member access has been updated successfully.',
        },
      },
    });
    reset({ companyIds: values.companyIds }, { keepDirty: false });
  };

  // disable when  member is company admin (they have access to all companies)
  const tableDisabled = member.scope === OrganizationMemberItemScope.ADMIN || !organizationId;
  const buttonDisabled = tableDisabled || !formState.isDirty || loading;
  const selectAll = member.scope === OrganizationMemberItemScope.ADMIN;

  return (
    <Stack
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      direction="column"
      paddingY={4}
      fullWidth
      fullHeight
      justifyContent="space-between"
    >
      <MemberScopeTable
        selectedCompanyIds={companyIds}
        onChangeSelectedCompanyIds={handleCompanySelect}
        disabled={tableDisabled}
        selectAll={selectAll}
      />
      <StyledStack justifyContent="end" paddingY={5} paddingX={5}>
        <StyledButton size="sm" variant="primary" disabled={buttonDisabled} rounded="regular" type="submit">
          Save
        </StyledButton>
      </StyledStack>
    </Stack>
  );
};
